import * as d3 from 'd3';
import type { MigrationArc } from '../domain/MigrationArc';
import type { AncestorGraph } from '../domain/AncestorGraph';
import { AppStore } from '../store/AppStore';
import { TimelineStore } from '../store/TimelineStore';
import { SelectionStore } from '../store/SelectionStore';
import { Component } from './Component';
import type { MapView } from './MapView';
import type { DotCell } from '../domain/DotCell';
import { anchorFor, cellKey } from '../utils/CellKey';
import { createLogger } from '../utils/logger';

const log = createLogger('ArcLayer');

// Number of great-circle sample points per arc. 80 gives smooth curves
// across trans-Atlantic distances without perceptible segmentation.
const ARC_SAMPLES = 80;

export class ArcLayer extends Component {
  private arcGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  /** Latest dot cells — line ends are drawn at their cell's anchor. */
  private dotMap: Map<string, DotCell> = new Map();

  constructor(private readonly mapView: MapView) {
    super();
  }

  render(): HTMLElement {
    // ArcLayer renders into the MapView SVG group, not an HTML element.
    // This return satisfies the abstract base class; the element is never appended.
    return document.createElement('span');
  }

  mount(_parent: HTMLElement): void {
    this.arcGroup = d3
      .select(this.mapView.getLayerGroup())
      .append('g')
      .attr('class', 'arc-layer')
      .attr('data-testid', 'arc-layer')
      .attr('aria-hidden', 'true'); // decorative SVG — accessible data in BranchPanel

    this.track(AppStore.subscribe(() => this._redraw()));
    this.track(TimelineStore.subscribe(() => this._redraw()));
    this.track(SelectionStore.subscribe(() => this._redraw()));
    this.track(this.mapView.onViewChanged(() => this._reproject()));

    this._redraw();
    log.info('ArcLayer mounted');
  }

  unmount(): void {
    this.arcGroup?.remove();
    super.unmount(); // fires tracked unsubs; this.el is never set so el?.remove() is a no-op
  }

  // ── Private ────────────────────────────────────────────────────────────────

  /** Reproject all existing arc paths when the map pans or zooms. No animation. */
  private _reproject(): void {
    this.arcGroup
      .selectAll<SVGPathElement, MigrationArc>('path.arc')
      .attr('d', (d) => this._arcPath(d));
  }

  /** Full data join. Called when any store changes. Animates new arcs entering. */
  private _redraw(): void {
    const { arcs, branchPaths, graph, dotMap } = AppStore.getState();
    const { currentDecade } = TimelineStore.getState();
    const { generationCap, activeBranchId } = SelectionStore.getState();
    this.dotMap = dotMap;

    const colorMap = new Map(branchPaths.map((p) => [p.branchId, p.branchColor]));
    const gens = graph ? computeGenerations(graph) : new Map<string, number>();
    const visible = filterVisibleArcs(arcs, currentDecade, generationCap, gens);

    const arcOpacity = (d: MigrationArc) =>
      activeBranchId === null || d.branchId === activeBranchId ? 0.7 : 0.1;

    const join = this.arcGroup
      .selectAll<SVGPathElement, MigrationArc>('path.arc')
      .data(visible, (d) => `${d.person.id}:${d.from.raw}:${d.to.raw}:${d.year}`);

    // EXIT
    join.exit().remove();

    // UPDATE — reproject and re-apply opacity (handles isolate toggling)
    join.attr('d', (d) => this._arcPath(d)).attr('stroke-opacity', arcOpacity);

    // ENTER — draw and animate
    const entered = join
      .enter()
      .append('path')
      .attr('class', 'arc')
      .attr('fill', 'none')
      .attr('stroke', (d) => colorMap.get(d.branchId) ?? '#999999')
      .attr('stroke-width', 2)
      .attr('stroke-opacity', arcOpacity)
      .attr('d', (d) => this._arcPath(d));

    // Stroke-dasharray animation: draw each arc over 400ms, then remove the
    // dasharray so the full path always renders after pan/zoom reprojection.
    // If dasharray stays set, zooming in increases the projected path length
    // beyond the original pixel value — the stroke is then visually clipped short.
    entered.each(function () {
      const len = this.getTotalLength();
      d3.select(this)
        .attr('stroke-dasharray', `${len} ${len}`)
        .attr('stroke-dashoffset', len)
        .transition()
        .duration(400)
        .ease(d3.easeLinear)
        .attr('stroke-dashoffset', 0)
        .on('end', function () {
          d3.select(this).attr('stroke-dasharray', null).attr('stroke-dashoffset', null);
        });
    });

    log.debug(`ArcLayer: ${visible.length} arcs visible at decade ${currentDecade}`);
  }

  /**
   * Produce an SVG path string for one arc.
   *
   * Uses d3.geoInterpolate to sample ARC_SAMPLES points along the great circle,
   * then projects each via Leaflet and connects with d3.line. This bypasses
   * d3.geoPath's spherical clipping pipeline, which splits trans-Atlantic arcs
   * at certain zoom levels when one endpoint is far off-screen.
   */
  private _arcPath(arc: MigrationArc): string {
    // Ends are drawn at their cell's dot anchor, so the line meets the dot (P-15f)
    const from = anchorFor(this.dotMap, arc.from.lat!, arc.from.lng!);
    const to = anchorFor(this.dotMap, arc.to.lat!, arc.to.lng!);
    const interp = d3.geoInterpolate([from.lng, from.lat], [to.lng, to.lat]);
    const points: [number, number][] = d3.range(ARC_SAMPLES + 1).map((i) => {
      const [lng, lat] = interp(i / ARC_SAMPLES);
      const pt = this.mapView.projectPoint(lat, lng);
      return [pt.x, pt.y];
    });
    return d3.line()(points) ?? '';
  }
}

// ── Module-level pure helpers (exported for unit tests) ──────────────────────

/**
 * BFS from graph root to compute generation depth for every person.
 * Root = generation 0; root's parents = generation 1; etc.
 * Used by ArcLayer to apply the generationCap filter.
 */
export function computeGenerations(graph: AncestorGraph): Map<string, number> {
  const result = new Map<string, number>();
  const queue: Array<[string, number]> = [[graph.rootId, 0]];
  while (queue.length > 0) {
    const [id, depth] = queue.shift()!;
    if (result.has(id)) {
      continue;
    }
    const person = graph.persons.get(id);
    if (!person) {
      continue; // skip ids not present in the graph (e.g. rootId with empty persons map)
    }
    result.set(id, depth);
    if (person.fatherId) {
      queue.push([person.fatherId, depth + 1]);
    }
    if (person.motherId) {
      queue.push([person.motherId, depth + 1]);
    }
  }
  return result;
}

/**
 * Filter arcs to those visible at the given decade and within the generation cap.
 * An arc whose ends fall in the same ~25 km dot cell shows no move and is dropped
 * (P-15f). Exported for unit tests; ArcLayer and WaypointLayer call this.
 */
export function filterVisibleArcs(
  arcs: MigrationArc[],
  currentDecade: number,
  generationCap: number,
  generations: Map<string, number>,
): MigrationArc[] {
  return arcs.filter(
    (a) =>
      a.year > 0 &&
      a.year <= currentDecade &&
      a.from.lat !== null &&
      a.from.lat !== undefined &&
      a.from.lng !== null &&
      a.from.lng !== undefined &&
      a.to.lat !== null &&
      a.to.lat !== undefined &&
      a.to.lng !== null &&
      a.to.lng !== undefined &&
      cellKey(a.from.lat, a.from.lng) !== cellKey(a.to.lat, a.to.lng) &&
      (generations.get(a.person.id) ?? 0) <= generationCap,
  );
}
